import React, { useEffect, useState } from "react";
import { Box, Modal, Divider, FormControl, Typography, IconButton } from "@mui/material";
import Grid from "@mui/system/Grid";
import { isEmpty } from "lodash";

import { FeatureAccess, ListOptions, MasterRole } from "../../../models/Master";
import { flexAllCentre, flexAllEnd } from "../../../styles/mui/styles/display";
import {
  AddUserInputStyle,
  AddUserModalBox,
  AddUserModalGrid,
  AutocompleteDropDownMaxWidth,
  ContentScrollable,
} from "../style/style";
import {
  ButtonCloseStyle,
  ButtonVerifyStyle,
  FaXmarkIcon,
  HorizontalDivider,
  ModalGrid,
} from "../../../app/styles/styles";
import { ButtonContained } from "../../../app/common/button";
import { useForm } from "react-hook-form";
import { TextLabel } from "../../../app/common/TextLabel";
import colors from "../../../styles/colors/colors";
import { rules } from "../../../utils/validation/Validation";
import {
  DropDownPadding,
  InputTextLabel,
} from "../../../components/styles/styles";
import { FormInputTextStandard } from "../../../components/FormInputTextStandard";
import { ServiceProvider } from "../../../models/ServiceProvider";
import { DefaultPayloadUser } from "../../../constants/User";
import { MyAccountModel } from "../../../models/MyAccount";
import { FeatureId, UserRole } from "../../../constants/HomePage";
import {
  getAgencyListByLoggedInUser,
  getFilteredRoleType,
  getRoleById,
} from "../../../app/common";
import { PhoneNumberInput } from "../../../components/PhoneNumberInput";
import { FormInputDropdown } from "../../../components/FormInputDropdown";
import AgencyConfirmationModal from "./AgencyConfirmationModal";
import { FormAutocompleteSelect } from "../../../components/FormAutocompleteSelect";
import { getHealthPlanValueType, getValue } from "../../../utils";

export interface PropsFromState {
  masterRoleState: MasterRole[];
  masterServiceProviderState: ListOptions[];
  masterServiceProviderDefaultList: ServiceProvider[];
  masterMfaPreferenceState: any;
  setModalflag: (val: boolean) => void;
  postCreateUser: (payload: MyAccountModel) => void;
  modalFlag: boolean;
  addUserState: MyAccountModel;
  loggedInUser: MyAccountModel;
  healthPlanState: ListOptions[];
  masterFeatureAccessState: FeatureAccess[] | null;
}

export interface Data {
  role: any;
  healthPlan: any;
  agency: string[];
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
}

const AddUser: React.FC<any> = ({
  modalFlag,
  setModalflag,
  masterRoleState,
  masterServiceProviderState,
  postCreateUser,
  masterServiceProviderDefaultList,
  loggedInUser,
  healthPlanState,
  masterFeatureAccessState
}) => {
  const methods = useForm<any>({
    defaultValues: "",
  });
  const { handleSubmit, control, setValue, getValues, watch } = methods;
  const [isTangoAdmin, setIsTangoAdmin] = useState<boolean>(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [selectedAgencies, setSelectedAgencies] = useState<string[]>([]);
  const [masterRoleData, setMasterRoleData] = useState<ListOptions[]>([]);
  const [showHealthPlan, setShowHealthPlan] = useState(false);

  const hasHppSsoAccessForTangoAdmin = (
    featureAccess: FeatureAccess[] | null,
  ): boolean => {
    if (!featureAccess) return false;

    return featureAccess.some(
      (feature: FeatureAccess) =>
        feature?.featureId === FeatureId.HPP_SSO &&
        getValue(loggedInUser, "providerPortalUser.roleType") === UserRole.TANGO_ADMIN &&
        feature?.frontEndIsActive === true,
    );
  };

  useEffect(() => {
    const subscription = watch((_value, { name }) => {
      if (name === "role") {
        const role = getValues("role");
        const selectedRole = getRoleById(role, masterRoleData);
        setIsTangoAdmin(selectedRole === UserRole.TANGO_ADMIN);
        setShowHealthPlan(role === "HEALTH_PLAN_USER");
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, masterRoleData]);

  useEffect(() => {
    if (!isEmpty(masterRoleState)) {
      const providerUser = masterRoleState.find(
        (item: any) => item.label === UserRole.PROVIDER_USER
      );

      if (!providerUser) return; 
      
      if (hasHppSsoAccessForTangoAdmin(masterFeatureAccessState)) {
        setMasterRoleData([
          ...masterRoleState,
          {
             label: UserRole.HEALTHPLAN_USER,
             value: "HEALTH_PLAN_USER",
          },
        ]);
      } else {
       setMasterRoleData(masterRoleState);
      }
    }
  }, [masterRoleState, masterFeatureAccessState]);
  const onSubmit = (data: Data) => {
    const payload = DefaultPayloadUser;
    const roleId =
      data.role === "HEALTH_PLAN_USER"
        ? masterRoleState.find((x: any) => x.label === UserRole.PROVIDER_USER)
            ?.value
        : data.role;
    postCreateUser({
      ...payload,
      providerPortalUser: {
        ...payload.providerPortalUser,
        email: data.email,
        phoneNumber: data.phoneNumber,
        firstName: data.firstName,
        lastName: data.lastName,
        roleId: roleId,
        mfaId: 2,
        userName: `${data.firstName} ${data.lastName}`,
        registrationDate: new Date(),
        approver: loggedInUser?.providerPortalUser?.email,
        healthPlan: getHealthPlanValueType(data.healthPlan, 1) ?? null,
      },
      userProviderList: getFilteredAgency(data.agency),
    });
  };

  const serviceProviderList = (defaultProvider: any) => {
    const customizedArray =
      masterServiceProviderDefaultList &&
      masterServiceProviderDefaultList
        .filter(
          (item: any) =>
            defaultProvider &&
            defaultProvider.some(
              (itemSelectedProvider: ListOptions) =>
                itemSelectedProvider.value === item.serviceProviderId
            )
        )
        .map((item: any) => ({
          userId: loggedInUser.providerPortalUser.userId,
          providerId: item.providerId,
          dateCreated: null,
          name: item.providerName,
          formattedAddress: item.providerAddress,
          city: item.providerCity,
          state: item.providerState,
          phoneNumber: item.phone,
          npiId: item.providerNpi,
          serviceProviderId: item.serviceProviderId,
          taxId: item.taxId,
        }));
    return customizedArray;
  };

  const getFilteredAgency = (defaultProvider: any) => {
    return serviceProviderList(defaultProvider);
  };

  const submit = handleSubmit((data: Data) => {
    if (isTangoAdmin) {
      onSubmit(data);
    } else {
      const agencies = getValues("agency") || [];
      setSelectedAgencies(agencies);
      setConfirmationModalOpen(true);
    }
  });

  useEffect(() => {
    if (isEmpty(getValues("agency"))) {
      setValue("agency", []);
    }
  });

  return (
    <>
      <Modal
        open={modalFlag}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={flexAllCentre}
      >
        <Box sx={AddUserModalBox(false)}>
          <Grid container mb="0.4rem">
            <Grid size={{ xs: 11.5 }}>
              <Typography id="modal-modal-title" variant="h1">
                Add User
              </Typography>
            </Grid>
            <Grid size={{ xs: 0.5 }}>
              <IconButton onClick={() => setModalflag(false)}>
                <FaXmarkIcon />
              </IconButton>
            </Grid>
          </Grid>
          <Divider sx={HorizontalDivider} />
          <Box sx={ModalGrid}>
            <Typography variant="h5" sx={{ color: colors.fonts[6] }}>
              Adding a new user will allow them the right to see all members
              authorizations added to the NPIs selected below.
            </Typography>
          </Box>
          <Box mb="2rem" sx={ContentScrollable}>
            <Grid container>
              <Grid size={{ xs: 6 }} mb={3} sx={DropDownPadding}>
                {TextLabel("Role :", true)}
                <FormControl
                  variant="standard"
                  fullWidth
                  sx={AutocompleteDropDownMaxWidth}
                >
                  <FormInputDropdown
                    name="role"
                    control={control}
                    helper={rules.roleType}
                    list={
                      loggedInUser.providerPortalUser.roleType ===
                      UserRole.PROVIDER_ADMIN
                        ? getFilteredRoleType(masterRoleData)
                        : masterRoleData
                    }
                    disabled={false}
                  />
                </FormControl>
              </Grid>
              {showHealthPlan && (
                <Grid size={{ xs: 6 }} mb={3} sx={DropDownPadding}>
                  {TextLabel("Health Plan :", true)}
                  <FormControl
                    variant="standard"
                    fullWidth
                    sx={AutocompleteDropDownMaxWidth}
                  >
                    <FormInputDropdown
                      name="healthPlan"
                      control={control}
                      helper={rules.healthPlan}
                      list={healthPlanState}
                    />
                  </FormControl>
                </Grid>
              )}
              {!isTangoAdmin && (
                <Grid size={{ xs: 12 }} mb={3} sx={DropDownPadding}>
                  {TextLabel("Agency :", !isTangoAdmin ? true : false)}
                  <FormControl
                    variant="standard"
                    fullWidth
                    sx={AutocompleteDropDownMaxWidth}
                  >
                    <FormAutocompleteSelect
                      name="agency"
                      control={control}
                      style={AddUserInputStyle}
                      list={
                        loggedInUser.providerPortalUser.roleType !==
                        UserRole.TANGO_ADMIN
                          ? getAgencyListByLoggedInUser(
                              masterServiceProviderDefaultList,
                              loggedInUser
                            )
                          : masterServiceProviderState
                      }
                      multiple={true}
                      helper={!isTangoAdmin ? rules.agencyRequired : {}}
                      mandatorySymbol={true}
                    />
                  </FormControl>
                </Grid>
              )}
            </Grid>
            <Grid container pb={3}>
              <Grid size={{ xs: 6 }}>
                <FormControl
                  variant="standard"
                  fullWidth
                  sx={{ paddingRight: 4 }}
                >
                  <FormInputTextStandard
                    name="firstName"
                    control={control}
                    label="First Name:"
                    textLength={100}
                    readOnly={false}
                    helper={rules.firstName}
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <FormControl
                  variant="standard"
                  fullWidth
                  sx={{ paddingRight: 4 }}
                >
                  <FormInputTextStandard
                    name="lastName"
                    control={control}
                    label="Last Name:"
                    textLength={100}
                    readOnly={false}
                    helper={rules.lastName}
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Grid container pb={2}>
              <Grid size={{ xs: 6 }}>
                <FormControl
                  variant="standard"
                  fullWidth
                  sx={{ paddingRight: 4 }}
                >
                  <PhoneNumberInput
                    name="phoneNumber"
                    control={control}
                    style={InputTextLabel}
                    helper={rules.phoneNumber}
                    disabled={false}
                    mandate={true}
                    label={"Phone Number: "}
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <FormControl
                  variant="standard"
                  fullWidth
                  sx={{ paddingRight: 4 }}
                >
                  <FormInputTextStandard
                    name="email"
                    control={control}
                    label="Email:"
                    textLength={100}
                    readOnly={false}
                    helper={rules.email}
                    mandatorySymbol={true}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Box>
          <Divider sx={HorizontalDivider} />
          <Grid container sx={AddUserModalGrid}>
            <Grid size={{ xs: 12 }} sx={flexAllEnd}>
              <ButtonContained
                id="btnSubmitAddUser"
                onClick={submit}
                label="Submit"
                disabled={false}
                style={ButtonVerifyStyle}
              />
              <ButtonContained
                id="btnCloseAddUser"
                onClick={() => setModalflag(false)}
                label="Cancel"
                disabled={false}
                style={ButtonCloseStyle}
              />
            </Grid>
          </Grid>
        </Box>
      </Modal>
      {confirmationModalOpen && (
        <AgencyConfirmationModal
          open={confirmationModalOpen}
          selectedAgencies={selectedAgencies}
          onClose={() => setConfirmationModalOpen(false)}
          onConfirm={() => {
            setConfirmationModalOpen(false);
            handleSubmit(onSubmit)();
          }}
        />
      )}
    </>
  );
};

export default AddUser;
